import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Group,
  Button,
  PageHeader,
  Paper,
  Alert,
  Loader,
  Avatar,
  ConfirmDialog,
  DetailField,
} from '@vipso/shared';
import { useGetProductQuery, useDeleteProductMutation } from '../api/tenantApi';
import { ApprovalStatusBadge } from '../components/ApprovalStatusBadge';
import { EntityApprovalPanel } from '../components/EntityApprovalPanel';

export function ProductDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetProductQuery(id!, { skip: !id });
  const [remove, { isLoading: deleting }] = useDeleteProductMutation();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const product = data?.getProduct;

  async function handleDelete() {
    if (!product) return;
    await remove(product.id).unwrap();
    setConfirmOpen(false);
    navigate('/products');
  }

  if (isLoading) {
    return (
      <Stack align="center" py="xl">
        <Loader />
      </Stack>
    );
  }

  if (isError || !product) {
    return (
      <Alert color="red" title={t('products.notFound')}>
        <Button variant="light" mt="sm" onClick={() => navigate('/products')}>
          {t('products.backToList')}
        </Button>
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <PageHeader
        title={product.name}
        description={t('products.details')}
        actions={
          <Group>
            <Button variant="default" onClick={() => navigate('/products')}>
              {t('common.back')}
            </Button>
            <Button variant="light" onClick={() => navigate(`/products/${product.id}/edit`)}>
              {t('common.edit')}
            </Button>
          </Group>
        }
      />

      <Paper p="lg" withBorder radius="md">
        <Stack gap="md">
          {product.imageUrl ? (
            <Avatar src={product.imageUrl} size={96} radius="md" alt={product.name} />
          ) : null}
          <DetailField label={t('products.unit')} value={product.unit} />
          <DetailField label={t('products.category')} value={product.category?.name} />
          <DetailField
            label={t('approval.status')}
            value={<ApprovalStatusBadge status={product.approvalStatus} />}
          />
          <DetailField label={t('products.productDescription')} value={product.description} />
          <DetailField
            label={t('products.quantity')}
            value={product.quantity != null ? String(product.quantity) : undefined}
          />
          <DetailField
            label={t('products.pricePerQuantity')}
            value={
              product.pricePerQuantity != null
                ? `₹${Number(product.pricePerQuantity).toFixed(2)}`
                : undefined
            }
          />
          <DetailField
            label={t('products.subsequentQuantity')}
            value={product.showSubse ? t('common.yes') : t('common.no')}
          />
          <DetailField
            label={t('products.addOnItem')}
            value={product.addOnItem ? t('common.yes') : t('common.no')}
          />
          {product.addOnItem ? (
            <DetailField label={t('products.addOnItemName')} value={product.addOnItemName} />
          ) : null}
        </Stack>
      </Paper>

      <EntityApprovalPanel
        subjectType="PRODUCT"
        subjectId={product.id}
        entityApprovalStatus={product.approvalStatus}
      />

      <Group>
        <Button onClick={() => navigate(`/orders?productIds=${product.id}`)}>
          {t('common.transactions')}
        </Button>
        <Button variant="default" onClick={() => navigate('/orders')}>
          {t('products.addRecord')}
        </Button>
        <Button color="red" variant="light" onClick={() => setConfirmOpen(true)}>
          {t('common.delete')}
        </Button>
      </Group>

      <ConfirmDialog
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void handleDelete()}
        title={t('products.deleteConfirm')}
        message={t('common.deleteMessage', { name: product.name })}
        confirmLabel={t('common.delete')}
        loading={deleting}
        danger
      />
    </Stack>
  );
}
